import { useSelector } from "react-redux";
import { RootState } from "../../reducers/RootReducer";
import { useCallback, useMemo, useState } from "react";
import { compareObjects } from "../../Utils/compareObjects";
import { DiamondLabels, weddingButton } from "../../Utils/commonData";
import { getLocalStorageData } from "../../Utils/general";
import CONSTANTS from "../../constant/general";

export const useFinalizeYourDesign = () => {
  const ring = useSelector((state: RootState) => state.ring);
  const footer = useSelector((state: RootState) => state.footer);
  const settingFilter = useSelector((state: RootState) => state.settingFilter);
  const cart = useSelector((state: RootState) => state.cart);
  const stateManager = useSelector((state: RootState) => state.stateManager);
  const diamond = useSelector((state: RootState) => state.diamond);
  const table = useSelector((state: RootState) => state.table);
  const filter = useSelector((state: RootState) => state.filter);
  const rpid = getLocalStorageData(CONSTANTS.RPID);
  const builder_mode = getLocalStorageData(CONSTANTS.BUILDER_MODE_KEY);
  const uuid = getLocalStorageData(CONSTANTS.DESIGN_UUID);
  const hbuuid = getLocalStorageData(CONSTANTS.HBUUID);
  const [ loading, setLoading ] = useState(false);
  const { Label, image_cache_param } = ring.extraData;
  const {
    Ring_Style,
    Diamond_Type,
    Diamond_Shape,
    Center_Diamond_Size,
    Ring_Crown,
    Ring_Side,
    Crown_Color,
    Color,
    Crown_Metal,
    Metal,
    Wedding_Band,
    Single_First_Metal_Band,
    Double_First_Metal_Band,
    Double_Second_Metal_Band,
    First_Band_Engraving,
    Second_Band_Engraving,
    Earring_Back,
    Diamond_Quality,
    Side_Stones
  } = ring.options;


  const { image } = cart;
  const {
    Ring_Crown:    RingCrownName,
    Ring_Side:     RingSideName,
    Ring_Style:    RingStyleName,
    Diamond_Shape: DiamondShapeName,
    Diamond_Type:     DiamondTypeName,
    Color:         ColorName,
    Crown_Color:   CrownColorName,
    Crown_Metal:   CrownMetalName,
    Metal:         MetalName,
    Ring_Size:     RingSizeName,
    Engraving:     EngravingName
  } = ring.selectedRingDetails;
  const {
    source,
    carat,
    shape,
  } = diamond.details;

  const { ring_tcw, bands_tcw, ring_price, left_band_price, right_band_price } = ring.ringPrice;

  const customisationAllowed = useMemo(() => {
    return ring.extraData.customisation_allowed;
  }, [ ring.extraData.customisation_allowed ]);

  const isSideStone = useMemo(() => ring.options.Side_Stones === 'DIA' ? '' : `and ${ring.selectedRingDetails.Side_Stones}`, [ ring.options.Side_Stones, ring.selectedRingDetails.Side_Stones ]);

  const FirstBandMetalColor = useMemo(() => {
    return weddingButton.find(btn => btn.Code === (Single_First_Metal_Band || Double_First_Metal_Band))?.Name;
  }, [ weddingButton, Single_First_Metal_Band, Double_First_Metal_Band ]);

  const SecondBandMetalColor = useMemo(() => {
    return weddingButton.find(btn => btn.Code === Double_Second_Metal_Band)?.Name;
  }, [ weddingButton, Double_Second_Metal_Band ]);

  const FirstBand = useMemo(() => {
    return {
      ...(ring.ringPrice.band_width && { "Band Width": `${ring.ringPrice.band_width}mm` }),
      ...(Wedding_Band !== 'No Band' && bands_tcw && { 'Minimum Band Total Weight': Wedding_Band === 'Single' ? `${bands_tcw.toFixed(2)} ct.` : `${(bands_tcw / 2).toFixed(2)} ct.` }),
      Metal: FirstBandMetalColor ? `${FirstBandMetalColor} ${FirstBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}` : '', ...(First_Band_Engraving && { engraving: First_Band_Engraving })
    };
  }, [ ring?.ringPrice?.band_width, Wedding_Band, ring.ringPrice, FirstBandMetalColor, First_Band_Engraving ]);

  const headColor = useMemo(() => {
    return customisationAllowed ? Crown_Color : Color;
  }, [ customisationAllowed, Crown_Color, Color ]);

  const SecondBand = useMemo(() => {
    return {
      ...(ring.ringPrice.band_width && { "Band Width": `${ring.ringPrice.band_width}mm` }),
      ...(Wedding_Band === 'Double' && bands_tcw && {
        'Minimum Band Total Weight': `${(bands_tcw / 2).toFixed(2)} ct.`
      }),
      Metal: SecondBandMetalColor ? `${SecondBandMetalColor} ${SecondBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}` : '', ...(Second_Band_Engraving && { engraving: Second_Band_Engraving })
    };
  }, [ Wedding_Band, ring.ringPrice, SecondBandMetalColor, Second_Band_Engraving ]);

  const generateName = useCallback((name: string, isStuds: boolean = false) => {
    let DiamondSize = ring.optionsData.Center_Diamond_Size && ring.optionsData.Center_Diamond_Size.find((size: any) => size.Code === Center_Diamond_Size);
    let centerDiamondSize = (diamond.details && diamond.details.carat) ? diamond.details.carat : DiamondSize !== undefined ? DiamondSize.Code / 100 : Center_Diamond_Size;
    if (Object.keys(ring.selectedRingDetails).length > 0) {
      switch (name) {
        case 'ringName': {
          if (isStuds) {
            return `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''}${RingStyleName} ${DiamondShapeName} ${DiamondTypeName} Diamond Stud Earrings`
          }
          return `${(Label?.toLowerCase() !== 'generic' && Label)? ((Label === 'Reve') ? 'rêve ' : `${Label} `): ''}${RingStyleName} ${source} Diamond ${isSideStone} ${DiamondLabels[(shape ? shape : DiamondShapeName) as string]} Engagement Ring${Wedding_Band !== 'No Band' ? (Wedding_Band === 'Single' ? ' with Wedding Band' : ' with Wedding Bands') : ''}`.trim();
        }

        case 'diamondName':
          return `${carat} ct. ${DiamondLabels[shape as string]} ${source} Diamond`;

        case 'settingName': {
          if (isStuds) {
            return `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' :`${Label}`) : ''} ${RingStyleName} ${DiamondShapeName} ${DiamondTypeName} Diamond Stud Earrings in ${MetalName}${ColorName ? ` ${ColorName}` : ''}${MetalName?.toLowerCase() !== 'platinum' ? ' Gold' : ''} (${ring_tcw ? (+ring_tcw + (+centerDiamondSize)).toFixed(2) : (+centerDiamondSize).toFixed(2)} ct. tw.)`.trim();
          }

          return `${(Label?.toLowerCase() !== 'generic' && Label)? ((Label === 'Reve') ? 'rêve ' :`${Label}`): ''} ${RingStyleName} ${source} Diamond ${isSideStone} ${DiamondLabels[(shape ? shape : DiamondShapeName) as string]} Engagement Ring in ${MetalName}${ColorName ? ` ${ColorName}` : ''}${MetalName?.toLowerCase() !== 'platinum' ? ' Gold' : ''} (${ring_tcw ? (+ring_tcw + (+centerDiamondSize)).toFixed(2) : (+centerDiamondSize).toFixed(2)} ct. tw.)`.trim();
        }

        case 'leftBandName':
          return `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''} ${source} Diamond Wedding Band in ${FirstBandMetalColor} ${FirstBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`.trim();

        case 'rightBandName':
          return `${(Label?.toLowerCase() !== 'generic' && Label) ? ((Label === 'Reve') ? 'rêve ' : `${Label} `) : ''} ${source} Diamond Wedding Band in ${SecondBandMetalColor} ${SecondBandMetalColor?.split(' ')[0]?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`.trim();

        default:
          return '';
      }
    }
  }, [ ring.selectedRingDetails, ring.options, ring.optionsData, ring.ringPrice, diamond.details ]);

  const showRingDetails = useMemo(() => {
    const isMetalNotPT = (CrownMetalName ?? MetalName)?.toLowerCase() !== "platinum";
    return {
      ...(ring.ringPrice.ring_width && { "Ring Width": `${ring.ringPrice.ring_width}mm` }),
      'Ring Style':  RingStyleName,
      'Ring Crown':  RingCrownName,
      'Ring Side':   RingSideName, ...(ring_tcw && { 'Minimum Setting Total Weight': `${ring_tcw.toFixed(2)} ct.` }), ...(customisationAllowed && { 'Crown Metal': `${CrownMetalName} ${CrownColorName ? CrownColorName : ''} ${CrownMetalName?.toLowerCase() !== 'platinum' ? 'Gold' : ''}` }),
      'Metal':       `${MetalName} ${ColorName ? ColorName : ''} ${MetalName?.toLowerCase() !== 'platinum' ? 'Gold' : ''}`,
      "Crown Metal": `${CrownMetalName ?? MetalName} ${isMetalNotPT ? (CrownColorName ?? ColorName) : ""} ${isMetalNotPT ? "Gold" : ""}`,
      'Ring Size':   RingSizeName, ...(EngravingName && { Engraving: EngravingName }),
      ...(ring.selectedRingDetails.Side_Stones && { 'Setting Accent Type': ring.options.Side_Stones === 'DIA' ? '' : `${ring.selectedRingDetails.Side_Stones}` })
    };
  }, [ ring?.ringPrice?.ring_width, ring.selectedRingDetails, ring.ringPrice, customisationAllowed ]);

  const handleFinalizeYourDesign = async (isStuds: boolean) => {
    setLoading(true);
    try {
      const ringSku = `${footer.styleID}-${Ring_Style}${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}-${Crown_Metal ?? Metal}-${Metal}-${Color}-${Diamond_Type}-${Side_Stones}${isStuds ? `-${Earring_Back}` : ''}`;
      const endpoint = process.env.REACT_APP_DESIGN_DATA_UPLOAD as string;

      const {
        diamond_details,
        ring_options_code,
        sku,
        setting_filter_options,
        ring_b2b_cost,
        table_row_id,
      } = stateManager.editData;

      const oldEditData = {
        ring_options_code,
        sku,
        setting_filter_options,
        ...(!isStuds && { table_row_id, ring_b2b_cost, diamond_details })
      };

      const newNames: { [key: string]: string } = {
        ring_name: generateName('ringName', isStuds) as string,
        ...(!isStuds && { diamond_name: generateName('diamondName') as string, }),
        setting_name: generateName('settingName',isStuds) as string
      };

      if (Wedding_Band === 'Single') {
        newNames['left_band_name'] = generateName('leftBandName',isStuds) as string;
      } else if (Wedding_Band === 'Double') {
        newNames['left_band_name'] = generateName('leftBandName',isStuds) as string;
        newNames['right_band_name'] = generateName('rightBandName',isStuds) as string;
      }

      const filteredExpandedRow = Object.fromEntries(Object.entries(table.expandRow).filter(([ _, value ]) => value !== null && value !== ''));

      const newEditData = {
        ...(!isStuds && { diamond_details: diamond.details, table_row_id: filteredExpandedRow, ring_b2b_cost: 111, }),
        ring_options_code:      ring.options,
        sku:                    ringSku,
        setting_filter_options: {
          ...settingFilter.options,
          maximumCaratSize: settingFilter.maximumCaratSize,
          selectedProduct:  settingFilter.selectedProduct
        },
      };

      const style = footer.styleID.includes('RP') ? `${footer.styleID}-${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}` : `${footer.styleID}-${!isStuds ? Ring_Style : ''}${Ring_Crown}-${Diamond_Shape}-${Center_Diamond_Size}-${Ring_Side}`;
      const uniqueName = footer.styleID.includes('RP') ? `${footer.styleID}${Ring_Crown}${Diamond_Shape}${Center_Diamond_Size}${Ring_Side}` : `${footer.styleID}${Ring_Style}${Ring_Crown}${Diamond_Shape}${Center_Diamond_Size}${Ring_Side}${headColor}${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `${Double_Second_Metal_Band?.split('_')[1]}` : ''}${Wedding_Band === 'Double' ? 'C1' : Wedding_Band === 'Single' ? 'C2' : 'C3'}`;
      const isSameObjects = compareObjects(oldEditData, newEditData);
      let returnUrl: string = '';
      let editUUID;
      let pricing = 0;
      if(isStuds){
        const diaQuality = Diamond_Quality;
        pricing=  diaQuality === "GOOD"? ring_price: diaQuality === "BETTER" ? left_band_price: diaQuality === "BEST" ? right_band_price : 0
      }else {
        pricing = +ring_price;
      }
      if (!isSameObjects && !cart.isOrderExist) {
        const result = await fetch(endpoint, {
          method: 'POST',
          body:   JSON.stringify({
            ...newEditData,
            names:           newNames,
            category: isStuds ? CONSTANTS.STUD_CATEGORY_CODE : CONSTANTS.RING_CATEGORY_CODE,
            setting_details: {
              ...ring.selectedRingDetails,
              Ring_Metal_Color:       showRingDetails['Metal']?.trim(),
              Ring_Crown_Metal_Color: showRingDetails['Crown Metal']?.trim(),
              ...{ Ring_First_Band_Metal: FirstBand.Metal ? FirstBand.Metal?.trim() : '' }, ...{ Ring_Second_Band_Metal: SecondBand.Metal ? SecondBand.Metal?.trim() : '' }
            },
            ring_extra_data:        { ...ring.extraData, exclusionOption: ring.exclusionOption },
            ...(!isStuds && {
              journey:                ring.journey,
              diamond_filter_options: {
                options:                  filter.options,
                afterRingSelectedOptions: filter.afterRingSelectedOptions
              },
            }),
            image_code:     `${style}/${headColor}-${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `-${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `-${Double_Second_Metal_Band?.split('_')[1]}` : ''}/${Wedding_Band === 'Double' ? 'C1' : Wedding_Band === 'Single' ? 'C2' : 'C3'}/`,
            image_urls: isStuds ? {
              "Primary": image[0],
              "Front":   image[1],
              "Cross":   image[2],
              "Side":    image[3],
              "Top":     image[4],
              "Back":    image[5],
              "Model":   image[6],
            } : {
              'Primary':        image[0],
              'Through Finger': image[1],
              'Top':            image[2],
              'Side':           image[3],
              'Hand':           image[4],
              ...(Wedding_Band && Wedding_Band !== 'No Band' ? { 'Only Ring': `${process.env.REACT_APP_IMAGE_KEY}/${style}/${headColor}-${Color}${(Single_First_Metal_Band || Double_First_Metal_Band) ? `-${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}` : ''}${Double_Second_Metal_Band ? `-${Double_Second_Metal_Band?.split('_')[1]}` : ''}/C3/V1/2000/fafafa/hq/${uniqueName}V12000.jpg?${image_cache_param}` } : {}), ...(Wedding_Band && Wedding_Band !== 'No Band' ? { 'Left Band': `${process.env.REACT_APP_IMAGE_KEY}/${style}/${(Single_First_Metal_Band || Double_First_Metal_Band)?.split('_')[1]}/C4/V1/2000/fafafa/hq/${uniqueName}V12000.jpg?${image_cache_param}` } : {}), ...(Wedding_Band && Wedding_Band === 'Double' ? { 'Right Band': `${process.env.REACT_APP_IMAGE_KEY}/${style}/${(Double_Second_Metal_Band)?.split('_')[1]}/C4/V1/2000/fafafa/hq/${uniqueName}V12000.jpg?${image_cache_param}` } : {})
            },
            ring_b2c_price: +pricing,
            action:         builder_mode === CONSTANTS.EDIT ? 'update' : 'insert', ...(builder_mode === CONSTANTS.EDIT) && { short_uuid: uuid }
          })
        });
        if (result.ok) {
          const design_data = await result.json();
          if (design_data) {
            if (builder_mode === CONSTANTS.BUILD) {
              editUUID = await design_data.short_uuid;
              returnUrl = `${window.location.origin}${process.env.REACT_APP_REDIRECT_URL_PREFIX}pid=${rpid}&hkuuid=${await design_data.short_uuid}${hbuuid ? `&uuid=${hbuuid}` : ""}`;
            } else if (builder_mode === CONSTANTS.EDIT) {
              const uuid = getLocalStorageData(CONSTANTS.DESIGN_UUID);
              editUUID = uuid;
              returnUrl = `${window.location.origin}${process.env.REACT_APP_REDIRECT_URL_PREFIX}pid=${rpid}&hkuuid=${uuid}${hbuuid ? `&uuid=${hbuuid}` : ""}&action=updated`;
            }
          }
        }
      }
      if (isSameObjects || cart.isOrderExist) {
        editUUID = uuid;
        returnUrl = `${window.location.origin}${process.env.REACT_APP_REDIRECT_URL_PREFIX}pid=${rpid}&hkuuid=${uuid}${hbuuid ? `&uuid=${hbuuid}` : ""}`;
      }
      [ CONSTANTS.RPID, CONSTANTS.BUILDER_MODE_KEY, CONSTANTS.DESIGN_UUID ].forEach(key => localStorage.removeItem(key));
      if(!isStuds){
        const url = `${window.location.origin}${process.env.REACT_APP_EDIT_URL_PREFIX}#edit/rpid=${rpid}&hkuuid=${editUUID}&start_with=summary&tid=${Date.now()}`;
        window.history.pushState('back-url', '', url);
      }
      window.location.href = returnUrl;
    } catch (error: any) {
      console.error(error?.message || error);
      setLoading(false);
    }
  };

  return {
    handleFinalizeYourDesign,
    loading,
    showRingDetails,
    customisationAllowed,
    isSideStone,
    FirstBandMetalColor,
    SecondBandMetalColor,
    FirstBand,
    headColor,
    SecondBand,
    generateName
  };
};
